import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IDropdownOption } from '@fluentui/react';
import { FluentProvider, Tooltip, webLightTheme } from '@fluentui/react-components';
import React from 'react';
import { Controller, FieldValues, UseFormMethods, useWatch } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import LegoCategoryDropdown from '../../common/formFields/LegoCategoryDropdown';
import QueryBasedDropdown from '../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import AnticipatedTerminationFYDropdown from '../../common/formFields/AnticipatedTerminationFYDropdown';
import AnticipatedTerminationQuarterDropdown from '../../common/formFields/AnticipatedTerminationQuarterDropdown';
import {
  GET_ENTITY_STATUSES_QUERY,
  GET_LIFECYCLE_STATUSES_QUERY,
  GET_INTAKE_METHODS_QUERY,
  GET_TERMINATION_LIFECYCLE_STATUSES_QUERY,
} from '../../../utils/statApi/EntityStatusApi';
import IDropdownReference from '../../../utils/types/IDropdownReference';
import { ILifecycleStatus } from '../../../utils/types/ICompanyBusinessNature';

export interface IStatusInformation {
  legalStatus?: IDropdownReference;
  lifecycleStatus?: IDropdownReference;
  terminationLifecycleStatus?: IDropdownReference;
  blockerCategory?: IDropdownReference;
  intakeMethod?: IDropdownReference;
  anticipatedTerminationFY?: string;
  anticipatedTerminationQuarter?: string;
}

// Rule A: Lifecycle statuses that only allow Termination Lifecycle Status = '--' when
// Legal Status = Active, so Blocker Category and Intake Method are not applicable.
const NON_APPLICABLE_LIFECYCLE_STATUSES = ['Operating', 'TBD', 'Accounting Ledger Only'];

export const isBlockerAndIntakeNotApplicable = (
  legalStatusFieldName?: string,
  lifecycleStatusFieldName?: string,
): boolean =>
  legalStatusFieldName === 'Active' &&
  NON_APPLICABLE_LIFECYCLE_STATUSES.includes(lifecycleStatusFieldName ?? '');

// Rule B: allowed Termination Lifecycle Status field names keyed by Lifecycle Status field name.
const ALLOWED_TERMINATION_STATUSES_BY_LIFECYCLE: Record<string, string[]> = {
  Operating: ['--'],
  'Accounting Ledger Only': ['--'],
  TBD: ['--'],
  'Target for Termination': [
    'Active Integrating',
    'Active Wind Down',
    'Waiting for Term Review (Under Eligibility Review)',
    'Blocker',
    'Eligible',
    'In Process',
  ],
  'Finance Incomplete': ['In Process'],
  Merged: ['Completed'],
  Dissolved: ['Completed'],
  Sold: ['Completed'],
  Inactive: ['Completed'],
  'De-registered': ['Completed'],
  Amalgamated: ['Completed'],
};

// Rule C: When Legal Status is one of these, the Termination Lifecycle Status dropdown
// is restricted to only the listed field names, regardless of Lifecycle Status.
export const RESTRICTED_TERMINATION_STATUSES_BY_LEGAL_STATUS: Record<string, string[]> = {
  'Notices Pending': ['In Process'],
};

export const getTerminationLifecycleStatusIsSelectable = (
  terminationLifecycleFieldName: string | undefined,
  legalStatusFieldName: string | undefined,
  lifecycleStatusFieldName: string | undefined,
): boolean => {
  if (!terminationLifecycleFieldName) return false;

  const restrictedByLegalStatus = legalStatusFieldName
    ? RESTRICTED_TERMINATION_STATUSES_BY_LEGAL_STATUS[legalStatusFieldName]
    : undefined;
  if (restrictedByLegalStatus) {
    return restrictedByLegalStatus.includes(terminationLifecycleFieldName);
  }

  const allowedByLifecycle = lifecycleStatusFieldName
    ? ALLOWED_TERMINATION_STATUSES_BY_LIFECYCLE[lifecycleStatusFieldName]
    : undefined;
  return allowedByLifecycle ? allowedByLifecycle.includes(terminationLifecycleFieldName) : false;
};

// Once a primary blocker exists, the category is driven by it and must be disabled
// and not required here, regardless of the (possibly reset-to-null) field value.
export const getBlockerCategoryFieldState = ({
  isBlocker,
  isBlockerAndIntakeDisabled,
  hasPrimaryBlocker,
}: {
  isBlocker: boolean;
  isBlockerAndIntakeDisabled: boolean;
  hasPrimaryBlocker?: boolean;
}): { required: boolean; disabled: boolean } => {
  const disabled = !isBlocker || isBlockerAndIntakeDisabled || !!hasPrimaryBlocker;
  return {
    disabled,
    required: isBlocker && !disabled,
  };
};

interface IEntityTerminationStatusEditFormProps {
  form: UseFormMethods<FieldValues>;
  statusInfo?: IStatusInformation;
  hasPrimaryBlocker?: boolean;
}

const EntityTerminationStatusEditForm = (
  props: IEntityTerminationStatusEditFormProps,
): JSX.Element => {
  const { form, statusInfo, hasPrimaryBlocker } = props;
  const { control, setValue, errors } = form;

  const watchLegalStatus = useWatch({
    control,
    name: 'legalStatus',
    defaultValue: statusInfo?.legalStatus,
  });

  const watchLifecycleStatus = useWatch({
    control,
    name: 'lifecycleStatus',
    defaultValue: statusInfo?.lifecycleStatus,
  });

  const watchTerminationLifecycleStatus = useWatch({
    control,
    name: 'terminationLifecycleStatus',
    defaultValue: statusInfo?.terminationLifecycleStatus,
  });

  const isBlocker = watchTerminationLifecycleStatus?.fieldName === 'Blocker';

  const isBlockerAndIntakeDisabled = isBlockerAndIntakeNotApplicable(
    watchLegalStatus?.fieldName,
    watchLifecycleStatus?.fieldName,
  );

  // The Blocker Category can only be edited here while the entity has no primary
  // blocker. Once a primary blocker exists, the category is driven by that primary
  // blocker and is read-only (and not required) here.
  const primaryBlockerExists = !!hasPrimaryBlocker;
  const blockerCategoryFieldState = getBlockerCategoryFieldState({
    isBlocker,
    isBlockerAndIntakeDisabled,
    hasPrimaryBlocker: primaryBlockerExists,
  });

  // Rule B: Terminating lifecycle statuses that make Intake Method required.
  const terminatingLifecycleStatuses = [
    'Target for Termination',
    'Finance Incomplete',
    'Merged',
    'Dissolved',
    'Sold',
    'Inactive',
    'De-registered',
    'Amalgamated',
  ];
  const isIntakeMethodRequired = terminatingLifecycleStatuses.includes(
    watchLifecycleStatus?.fieldName ?? '',
  );

  const { loading: intakeMethodsLoading, data: intakeMethodsData } =
    useQuery(GET_INTAKE_METHODS_QUERY);
  const intakeMethods =
    !intakeMethodsLoading && intakeMethodsData ? intakeMethodsData.intakeMethods : [];

  const { loading: entityStatusLoading, data: entityStatusData } =
    useQuery(GET_ENTITY_STATUSES_QUERY);
  const entityStatuses =
    !entityStatusLoading && entityStatusData ? entityStatusData.entityStatuses : [];

  const { loading: lifecycleStatusLoading, data: lifecycleStatusData } = useQuery(
    GET_LIFECYCLE_STATUSES_QUERY,
  );
  const lifecycleStatuses =
    !lifecycleStatusLoading && lifecycleStatusData
      ? lifecycleStatusData.lifecycleStatuses.map((ls: ILifecycleStatus) => ({
          ...ls,
          isSelectable:
            watchLegalStatus?.id != null && ls.entityStatusIds?.includes(watchLegalStatus.id),
        }))
      : [];

  const { loading: terminationLifecycleLoading, data: terminationLifecycleData } = useQuery(
    GET_TERMINATION_LIFECYCLE_STATUSES_QUERY,
  );

  const terminationLifecycleStatuses =
    !terminationLifecycleLoading && terminationLifecycleData
      ? terminationLifecycleData.terminationLifecycleStatuses.map((t: IDropdownReference) => ({
          ...t,
          isSelectable: getTerminationLifecycleStatusIsSelectable(
            t.fieldName,
            watchLegalStatus?.fieldName,
            watchLifecycleStatus?.fieldName,
          ),
        }))
      : [];

  const onLegalStatusChange = (newValue: IDropdownOption) => {
    setValue('legalStatus', newValue, { shouldValidate: true });
    setValue('lifecycleStatus', null);
    setValue('terminationLifecycleStatus', null);
    setValue('blockerCategory', null);
  };

  const onLifecycleStatusChange = (newValue: IDropdownOption) => {
    setValue('lifecycleStatus', newValue, { shouldValidate: true });
    setValue('terminationLifecycleStatus', null);
    setValue('blockerCategory', null);
  };

  const onTerminationLifecycleStatusChange = (newValue: IDropdownOption) => {
    setValue('terminationLifecycleStatus', newValue, { shouldValidate: true });
    setValue('blockerCategory', null);
  };

  const onDropdownChange = (valueKey: string) => (newValue: IDropdownOption) => {
    setValue(valueKey, newValue, { shouldValidate: true });
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <Controller
          name="legalStatus"
          control={control}
          defaultValue={statusInfo?.legalStatus ?? null}
          rules={{ required: 'Please select a Legal Status.' }}
          render={({ value }) => (
            <QueryBasedDropdown
              label="Legal Status"
              valueKey="legalStatus"
              required
              errors={errors}
              handleChange={onLegalStatusChange}
              value={value as IDropdownReference}
              dropdownOptionsData={entityStatuses}
            />
          )}
        />
        <Controller
          name="lifecycleStatus"
          control={control}
          defaultValue={statusInfo?.lifecycleStatus ?? null}
          rules={{ required: 'Please select a Lifecycle Status.' }}
          render={({ value }) => (
            <QueryBasedDropdown
              label="Lifecycle Status"
              valueKey="lifecycleStatus"
              required
              errors={errors}
              handleChange={onLifecycleStatusChange}
              value={value as IDropdownReference}
              dropdownOptionsData={lifecycleStatuses}
            />
          )}
        />
        <Controller
          name="terminationLifecycleStatus"
          control={control}
          defaultValue={statusInfo?.terminationLifecycleStatus ?? null}
          rules={{ required: 'Please select a Termination Lifecycle Status.' }}
          render={({ value }) => (
            <QueryBasedDropdown
              label="Termination Lifecycle Status"
              valueKey="terminationLifecycleStatus"
              required
              errors={errors}
              handleChange={onTerminationLifecycleStatusChange}
              value={value as IDropdownReference}
              dropdownOptionsData={terminationLifecycleStatuses}
            />
          )}
        />
        <Controller
          name="blockerCategory"
          control={control}
          defaultValue={statusInfo?.blockerCategory ?? null}
          rules={
            blockerCategoryFieldState.required
              ? { required: 'Please select a Blocker Category.' }
              : {}
          }
          render={({ value }) => {
            const blockerCategoryDropdown = (
              <LegoCategoryDropdown
                label="Blocker Category"
                valueKey="blockerCategory"
                required={blockerCategoryFieldState.required}
                disabled={blockerCategoryFieldState.disabled}
                errors={errors}
                handleChange={onDropdownChange('blockerCategory')}
                value={value}
                watchTerminationLifecycleStatus={watchTerminationLifecycleStatus}
              />
            );

            return primaryBlockerExists ? (
              <Tooltip
                content="The Blocker Category is managed via the entity's primary blocker on the current entity termination request."
                relationship="description"
                positioning="above"
              >
                <span style={{ display: 'inline-block' }}>{blockerCategoryDropdown}</span>
              </Tooltip>
            ) : (
              blockerCategoryDropdown
            );
          }}
        />
        <Controller
          name="intakeMethod"
          control={control}
          defaultValue={statusInfo?.intakeMethod ?? null}
          rules={isIntakeMethodRequired ? { required: 'Please select an Intake Method.' } : {}}
          render={({ value }) => (
            <QueryBasedDropdown
              label="Intake Method"
              valueKey="intakeMethod"
              required={isIntakeMethodRequired}
              disabled={isBlockerAndIntakeDisabled}
              errors={errors}
              handleChange={onDropdownChange('intakeMethod')}
              value={value as IDropdownReference}
              dropdownOptionsData={intakeMethods}
            />
          )}
        />
        <Controller
          control={control}
          name="anticipatedTerminationFY"
          defaultValue={statusInfo?.anticipatedTerminationFY ?? ''}
          render={({ value }): React.ReactElement => (
            <AnticipatedTerminationFYDropdown
              value={value}
              onChange={(val) => setValue('anticipatedTerminationFY', val)}
            />
          )}
        />
        <Controller
          control={control}
          name="anticipatedTerminationQuarter"
          defaultValue={statusInfo?.anticipatedTerminationQuarter ?? ''}
          render={({ value }): React.ReactElement => (
            <AnticipatedTerminationQuarterDropdown
              value={value}
              onChange={(val) => setValue('anticipatedTerminationQuarter', val)}
            />
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default EntityTerminationStatusEditForm;
